import heroImage from "./article_pics/pcap_triage.webp";
const article = {
   title: "PCAP Triage: A Custom Threat-Hunting Workbench for Packet Capture Files",
   published: "2025-07-23",
   readingTime: "10 min read",
   tags: ["Cybersecurity", "Python", "Threat Hunting", "Automation", "Network Analysis"],
   summary: "Frustrated by the lack of lightweight, customizable tools for analyzing PCAP files, I built my own—PCAP Triage. Here’s why and how it works.",
   favorite: true,
   image: heroImage,
   repoUrl: "https://github.com/Pb-22/pcap_triage",
   content: `
# PCAP Triage: A Custom Threat-Hunting Workbench for Packet Capture Files

Packet captures (PCAPs) are a goldmine of evidence for defenders—but only if you can efficiently extract what matters. I was searching for a tool that could help me ingest PCAPs and quickly pull out actionable insights for hunting threats and writing detections. Nothing quite fit the bill, so I wrote my own.

Meet **PCAP Triage**: a Python-powered GUI tool that turns raw packet captures into structured intelligence, highlights anomalies, and surfaces IOCs—all without requiring a massive learning curve or enterprise budget.

# Why I Built It

There are great tools out there—Wireshark, Zeek, Suricata—but most either require deep customization, generate too much noise, or do not synthesize findings into one human-readable report. I needed something lean, extensible, and fast. A tool I could run on a sample, get results in a few minutes, and use to guide my next steps in threat hunting or detection engineering.

I also wanted **modularity**. Most tools are tightly coupled or make it hard to bolt on new logic. PCAP Triage is structured from the ground up to support plug-and-play analysis logic.

# How It Works

1. **Choose a File**  
   The user selects a '.pcap' or '.pcapng' file via the GUI.

2. **Packet Counting**  
   The script attempts a fast count using 'tshark' or 'tcpdump'. If those fail, it falls back to PyShark.

3. **Analysis Loop**  
   Each packet is processed in a single pass. Dedicated components inside the 'PcapAnalyzer' class extract targeted insights: IPs, DNS, HTTP, SSL, creds, ARP, and more.

4. **Report Generation**  
   The output is rendered with Jinja2 into a full HTML report, which opens in your default browser.

# Analysis Components

Each logical component inside 'PcapAnalyzer' targets a specific category of network behavior. These components are independent, making them easy to enhance or replace.

## Public Destination IP Tracking

Captures all non-private destination IPs and logs the protocol stack used to reach them. Helps surface outbound C2 activity or data exfiltration.

## Port and Protocol Mismatch Detection

Compares destination ports against common defaults for protocols like HTTP, SSH, and DNS. Unusual combinations (e.g. HTTPS on port 21) are flagged for inspection.

## DNS Behavior Analysis

Inspects each DNS query for:
- Length and randomness (entropy)
- Rare or suspicious TLDs
- Failed queries
- TXT records often abused in malware infrastructure

## HTTP Inspection

Gathers and summarizes:
- HTTP request methods and URL counts
- Unique User-Agent strings
- MIME types
- Files or payloads transferred over HTTP
- Hosts serving uncommon user agents or suspicious content types

## File Transfer Logging

Collects metadata from file transfers:
- Source and destination IP
- MIME type
- File name and SHA256 hash
- Flags macro-enabled Office files or files from suspicious sources

## Flow Analysis and SYN Scan Detection

Builds per-destination flow statistics:
- Total packets and bytes
- Unique source IPs
- SYN-only packets
- Multi-source low-volume traffic patterns (often seen in scans)

## Beaconing Pattern Detection

Detects periodic traffic by analyzing timestamp intervals between packets. Alerts on consistent timing patterns often seen in malware beacons.

## Credential and Secret Exposure

Scans for leaked data across multiple protocols:
- HTTP Basic Auth credentials
- Email addresses
- API tokens and secrets
- FTP and Telnet login prompts
- SNMP community strings

## SSL/TLS Weakness Detection

Detects legacy SSL/TLS versions and weak cipher suites like RC4, MD5, EXPORT. Highlights hosts with misconfigurations or insecure setups.

## Layer 2 and Protocol Anomalies

Captures:
- Unsupported or rare protocol numbers
- Suspicious GRE or ICMP activity
- Duplicate IP or MAC mappings
- Gratuitous ARP broadcasts
- Noisy ARP activity

# Why These Modules Were Chosen

- 'pyshark' provides Wireshark-quality packet parsing without needing to build a custom decoder.
- 'tkinter' keeps the GUI simple and dependency-free.
- 'jinja2' allows full control over the HTML output format and structure.
- 'tshark' and 'tcpdump' are used to accelerate packet counting before PyShark kicks in.

Each of these external libraries was selected to streamline performance, maximize compatibility, and make the tool as portable as possible.

# Extending the Tool

You can easily add new inspection logic. Just create a method inside 'PcapAnalyzer'—for example, 'process_tls_ja3()'—and hook it into the 'process_packet()' function.

Because each analysis component is self-contained, updates or experiments will not affect the rest of the tool’s logic.

# Final Thoughts

PCAP Triage is not a replacement for Wireshark or Zeek—it is a focused triage assistant. When you want quick, prioritized insights from packet data without sifting through thousands of frames, this tool delivers.

Whether you are triaging a new malware sample or validating a detection rule, PCAP Triage saves time and gives you a structured starting point.

---

Want to try it out or contribute? [Get PCAP Triage on GitHub](https://github.com/Pb-22/pcap_triage).

Happy hunting.
  `,
};

export default article;
